import { useMutation, useQuery } from '@apollo/client'

import { useState } from 'react'
import { Navigate, useMatch, useNavigate, useParams } from 'react-router-dom'

import { Box } from '@grupoboticario/flora-react'

import type {
  AccelerationFormState,
  AccelerationStepperVariant,
  UpdateCampaignPayload
} from '@/components/AccelerationForm/AccelerationForm.types'
import AccelerationStepper from '@/components/AccelerationForm/AccelerationStepper'
import { getCampaignDiff } from '@/components/AccelerationForm/utils/getCampaignDiff'
import { mapCampaignQueryToFormFields } from '@/components/AccelerationForm/utils/mapCampaignQueryToFormFields'
import Loader from '@/components/Loader/Loader'
import { ModalEditConfirmation } from '@/components/ModalEditConfirmation'
import { ACCELERATION_STEPPER_VARIANT } from '@/constants/accelerationStepper'
import { useUserContext } from '@/context/user'
import UPDATE_CAMPAIGN from '@/graphql/mutation/updateCampaign'
import GET_CAMPAIGN_BY_ID from '@/graphql/queries/getCampaignById'
import { Actions } from '@/utils/dictionaries/actionsDictionary/actions'
import triggerToastError from '@/utils/triggerToastError/triggerToastError'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'

import * as P from '../pages.styles'

const EditAcceleratorCampaignPage = () => {
  const { campaignId } = useParams()
  const navigate = useNavigate()
  const editMatch = useMatch('/acceleration/:campaignId/edit')
  const { hasPermission } = useUserContext()
  const { user: loggedUser } = useUserContext()

  const [formData, setFormData] = useState<AccelerationFormState | null>(null)
  const [openEditConfirmationModal, setOpenEditConfirmationModal] = useState(false)

  const stepperVariant: AccelerationStepperVariant = editMatch
    ? ACCELERATION_STEPPER_VARIANT.EDIT
    : ACCELERATION_STEPPER_VARIANT.VIEW
  const isUnauthorizedEdit = Boolean(editMatch && !hasPermission(Actions.EDIT_ACCELERATION))

  const [updateCampaignCall, { loading: updateCampaignLoading }] = useMutation(UPDATE_CAMPAIGN)

  const { loading, data } = useQuery(GET_CAMPAIGN_BY_ID, {
    variables: {
      campaignId
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !campaignId || isUnauthorizedEdit
  })

  if (isUnauthorizedEdit) {
    return <Navigate to="/acceleration" replace />
  }

  const initialValues = mapCampaignQueryToFormFields(data?.getCampaign)

  const handlePublish = (values: AccelerationFormState) => {
    setFormData(values)
    setOpenEditConfirmationModal(true)
  }

  const publishCampaign = async () => {
    if (!formData || !campaignId) return

    const diff = getCampaignDiff(initialValues, formData)

    const payload: UpdateCampaignPayload = {
      ...diff,
      responsible: typeof loggedUser?.email === 'string' ? loggedUser.email : ''
    }

    const { data } = await updateCampaignCall({
      variables: {
        campaignId,
        campaign: payload
      }
    })

    if (data?.updateCampaign?.statusCode >= 400) {
      triggerToastError('Ops! Ocorreu um erro ao atualizar a campanha. Tente novamente.')
    }

    if (data?.updateCampaign?.statusCode === 204) {
      triggerToastSuccess(false, 'Campanha atualizada com sucesso!')
      setOpenEditConfirmationModal(false)
      setFormData(null)
      navigate(`/acceleration`)
    }
  }

  return (
    <Box css={P.pageWrapperCss}>
      {loading ? (
        <Loader />
      ) : (
        <AccelerationStepper
          variant={stepperVariant}
          isEditing
          initialValues={initialValues}
          onCancel={() => navigate('/acceleration')}
          onPublish={handlePublish}
        />
      )}
      <ModalEditConfirmation
        handleSubmit={publishCampaign}
        isLoading={updateCampaignLoading}
        openEditConfirmationModal={openEditConfirmationModal}
        setOpenEditConfirmationModal={setOpenEditConfirmationModal}
      />
    </Box>
  )
}

export { EditAcceleratorCampaignPage }
